import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { listPublishedPosts } from "@/lib/blog.functions";
import { browardCities } from "@/data/site";
import { citySlug as toCitySlug } from "@/lib/blog-shared";

export const Route = createFileRoute("/expert-tips/$citySlug/")({
  head: ({ params }) => {
    const cityName = params.citySlug === "general"
      ? "Broward County"
      : browardCities.find((c) => toCitySlug(c) === params.citySlug) || params.citySlug;
    const url = `https://mckay.steadyautogrowthdemo.com/expert-tips/${params.citySlug}`;
    return {
      meta: [
        { title: `${cityName} Pressure Washing Tips | Coastal Surface Experts` },
        { name: "description", content: `Local pressure washing and exterior cleaning tips for ${cityName} homes and businesses.` },
        { name: "keywords", content: `${cityName} pressure washing, ${cityName} soft wash, ${cityName} exterior cleaning tips, house washing ${cityName}, roof cleaning ${cityName}` },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "publisher", content: "Coastal Surface Experts" },
        { property: "og:title", content: `${cityName} Pressure Washing Tips` },
        { property: "og:description", content: `Practical exterior cleaning advice tailored to ${cityName} homes and businesses.` },
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
      ],
    };
  },
  component: CityListing,
});

function CityListing() {
  const { citySlug } = Route.useParams();
  const { data } = useQuery({
    queryKey: ["expert-tips-list", citySlug],
    queryFn: () => listPublishedPosts({ data: { citySlug, limit: 50 } }),
  });
  const posts = data?.posts ?? [];
  const cityName = citySlug === "general"
    ? "Broward County"
    : browardCities.find((c) => toCitySlug(c) === citySlug) || citySlug.replace(/-/g, " ");

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-gradient-hero text-primary-foreground">
          <div className="mx-auto flex min-h-screen max-w-5xl flex-col justify-center px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-24">
            <nav className="text-sm text-primary-foreground/80">
              <Link to="/expert-tips" className="hover:underline">Expert Tips</Link> <span>›</span> <span className="capitalize">{cityName}</span>
            </nav>
            <h1 className="mt-3 text-4xl font-extrabold sm:text-5xl text-balance">{cityName} pressure washing tips</h1>
          </div>
        </section>

        <section className="mx-auto max-w-5xl space-y-12 px-4 py-12 sm:px-6 lg:px-8">
          {posts.length === 0 ? (
            <p className="text-muted-foreground">No posts yet for this area. Check back soon.</p>
          ) : (
            (["residential", "commercial"] as const).map((aud) => {
              const group = posts.filter((p: any) => p.audience === aud);
              return (
                <div key={aud}>
                  <div className="mb-4 flex items-baseline justify-between border-b border-border pb-2">
                    <h2 className="text-2xl font-extrabold text-primary capitalize">{aud} tips</h2>
                    <span className="text-sm text-muted-foreground">{group.length} article{group.length === 1 ? "" : "s"}</span>
                  </div>
                  {group.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No {aud} posts yet for this area.</p>
                  ) : (
                    <div className="grid gap-5 sm:grid-cols-2">
                      {group.map((p: any) => (
                        <Link key={p.id} to="/expert-tips/$citySlug/$slug" params={{ citySlug: p.city_slug, slug: p.slug }} className="group rounded-xl border border-border bg-card p-5 hover:border-accent hover:shadow-lg">
                          <div className="text-xs font-bold uppercase tracking-wider text-accent">{p.audience}</div>
                          <h3 className="mt-2 font-bold text-primary group-hover:text-accent">{p.title}</h3>
                          {p.tldr && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{p.tldr}</p>}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
